import React from 'react';
import { FormattedMessage } from 'react-intl';

import colors from 'config/theme/colors';
import { createDictionary } from 'utils/ObjectUtils';

// Be careful of the order, it is used to order by type and for validation purpose
/**
 * @type {{
 *   [key in import('modules/selfServices/types').SelfServiceTypeEnum]: key in import('modules/selfServices/types').SelfServiceTypeEnum>
 * }}
 */
export const SELF_SERVICE_TYPE = {
  BREAKDOWN: 'BREAKDOWN',
  CHECK_IN: 'CHECK_IN',
  CHECK_OUT: 'CHECK_OUT'
} as const;

export type SelfServiceEnum = typeof SELF_SERVICE_TYPE[keyof typeof SELF_SERVICE_TYPE]

export const QUICK_CHECK_IN = 'QUICK_CHECK_IN';

// Be careful of the order, it is used to order by origin
/**
 * @type {{[key in import('modules/selfServices/types').SelfServiceOriginEnum]: key in import('modules/selfServices/types').SelfServiceOriginEnum}}
 */
export const SELF_SERVICE_ORIGIN = {
  HOME: 'HOME',
  KIOSK: 'KIOSK',
  ON_SITE: 'ON_SITE',
  MANUAL: 'MANUAL' // Used to manually complete check-out
};

/** @type {Array<import('modules/selfServices/types').SelfServiceOriginEnum} */
export const SELF_SERVICE_ORIGINS = Object.values(SELF_SERVICE_ORIGIN).filter(
  value => value !== SELF_SERVICE_ORIGIN.MANUAL
);
/** @type {Array<import('modules/selfServices/types').SelfServiceTypeEnum} */
export const SELF_SERVICE_TYPES = Object.values(SELF_SERVICE_TYPE);

/** @type {Array<import('modules/selfServices/types').SelfServiceTypeEnum} */
// This is useful for configuration UI
export const SORTED_SELF_SERVICE_TYPES = [
  ...SELF_SERVICE_TYPES.filter(type => type !== SELF_SERVICE_TYPE.BREAKDOWN),
  SELF_SERVICE_TYPE.BREAKDOWN
];

/** @type {Array<import('modules/selfServices/types').SelfServiceTypeEnum} */
export const CREATABLE_SELF_SERVICE_TYPES = [SELF_SERVICE_TYPE.CHECK_IN, SELF_SERVICE_TYPE.CHECK_OUT];

// Be careful of the order, it is used to compare status
/** @enum {import('modules/selfServices/types').SelfServiceStatusEnum} */
export const SELF_SERVICE_STATUS = {
  TO_BE_SENT: 'TO_BE_SENT',
  SENT: 'SENT',
  IN_PROGRESS: 'IN_PROGRESS',
  ANSWERED: 'ANSWERED'
};

/** @type {Array<import('modules/selfServices/types').SelfServiceStatusEnum>} */
export const ALL_SELF_SERVICE_STATUS = Object.values(SELF_SERVICE_STATUS);

export const SELF_SERVICE_STATUS_DASHBOARD = [SELF_SERVICE_STATUS.ANSWERED, SELF_SERVICE_STATUS.SENT];

export const SELF_SERVICE_STATS_REQUEST_TYPES = {
  COUNT_BY_PERIOD: 'COUNT_BY_PERIOD',
  COUNT_BY_STATUS: 'COUNT_BY_STATUS',
  DETAILED_STATS: 'DETAILED_STATS',
  HOUR_DISTRIBUTION: 'HOUR_DISTRIBUTION',
  MOBILITY_STATUS: 'MOBILITY_STATUS'
};

export const SELF_SERVICE_STATS_ORIGIN = {
  HOME: 'HOME',
  ON_SITE_OR_KIOSK: 'ON_SITE_OR_KIOSK'
};

export const SELF_SERVICE_STATS_HOUR_DISTRIBUTION_REDUCER = {
  [SELF_SERVICE_STATS_ORIGIN.HOME]: 'homeAnsweredOnHour',
  [SELF_SERVICE_STATS_ORIGIN.ON_SITE_OR_KIOSK]: 'onSiteOrKioskAnsweredOnHour'
};

export const SELF_SERVICE_STATS_STATUS_BY_ORIGIN = {
  [SELF_SERVICE_STATS_ORIGIN.HOME]: createDictionary({}, ['homeStatus']),
  [SELF_SERVICE_STATS_ORIGIN.ON_SITE_OR_KIOSK]: createDictionary(
    {
      [SELF_SERVICE_TYPE.CHECK_OUT]: ['onSiteStatus', 'kioskStatus', 'manualCheckOutCompleted']
    },
    ['onSiteStatus', 'kioskStatus']
  )
};

export const SELF_SERVICE_STATS_TIME_TO_COMPLETE_BY_ORIGIN = {
  [SELF_SERVICE_STATS_ORIGIN.HOME]: 'homeTimeToComplete',
  [SELF_SERVICE_STATS_ORIGIN.ON_SITE_OR_KIOSK]: 'onSiteOrKioskTimeToComplete'
};

export const detailStatisticsTab = [
  {
    type: SELF_SERVICE_TYPE.CHECK_IN,
    origin: SELF_SERVICE_STATS_ORIGIN.HOME
  },
  {
    type: SELF_SERVICE_TYPE.CHECK_IN,
    origin: SELF_SERVICE_STATS_ORIGIN.ON_SITE_OR_KIOSK
  },
  {
    type: SELF_SERVICE_TYPE.CHECK_OUT,
    origin: SELF_SERVICE_STATS_ORIGIN.HOME
  },
  {
    type: SELF_SERVICE_TYPE.CHECK_OUT,
    origin: SELF_SERVICE_STATS_ORIGIN.ON_SITE_OR_KIOSK
  }
];

const CUSTOMER_UPDATE_TYPE = {
  TEMPORARY: 'TEMPORARY',
  PERMANENTLY: 'PERMANENTLY'
};

export const CUSTOMER_UPDATE_TYPES = Object.values(CUSTOMER_UPDATE_TYPE);

export const SELF_SERVICE_KEY_STATUS = {
  USED: 'USED',
  IN_SLOT: 'IN_SLOT',
  RETRIEVED: 'RETRIEVED',
  NONE: 'NONE'
};

export const SELF_SERVICE_KEY_TYPES = {
  MOBILITY: 'MOBILITY',
  CUSTOMER: 'CUSTOMER'
};

export const VEHICLE_CHECK_MODEL_TYPE = {
  MANDATORY: 'MANDATORY',
  OPTIONAL: 'OPTIONAL',
  ADDITIONAL: 'ADDITIONAL'
};

export const FUEL_LEVEL = {
  FULL: 'FULL',
  THREE_QUARTERS: 'THREE_QUARTERS',
  HALF: 'HALF',
  ONE_QUARTER: 'ONE_QUARTER',
  RESERVE: 'RESERVE'
};

export const DOCUMENT_STATUS = {
  TO_VALIDATE: 'TO_VALIDATE',
  ACCEPTED: 'ACCEPTED',
  REFUSED: 'REFUSED',
  REQUESTED: 'REQUESTED',
  SUSPECTED_FRAUD: 'SUSPECTED_FRAUD',
  SKIPPED: 'SKIPPED'
};

export const DOCUMENT_STATUS_STYLE = {
  [DOCUMENT_STATUS.SKIPPED]: {
    label: (
      <FormattedMessage
        id={`checkIn.mobility.documents.status.${DOCUMENT_STATUS.SKIPPED}`}
        defaultMessage="Skipped"
      />
    )
  },
  [DOCUMENT_STATUS.REQUESTED]: {
    label: (
      <FormattedMessage
        id={`checkIn.mobility.documents.status.${DOCUMENT_STATUS.REQUESTED}`}
        defaultMessage="Waiting for customer"
      />
    )
  },
  [DOCUMENT_STATUS.TO_VALIDATE]: {
    label: (
      <FormattedMessage
        id={`checkIn.mobility.documents.status.${DOCUMENT_STATUS.TO_VALIDATE}`}
        defaultMessage="To validate"
      />
    )
  },
  [DOCUMENT_STATUS.ACCEPTED]: {
    background: colors['@success-color-light'],
    color: colors['@success-color-dark'],
    label: <FormattedMessage id="accepted" defaultMessage="Accepted" />
  },
  [DOCUMENT_STATUS.REFUSED]: {
    background: colors['@error-color-light'],
    color: colors['@error-color-dark'],
    label: (
      <FormattedMessage
        id="page.keyDeposit.appointments.details.management.documents.clientDocuments.medias.status.refused"
        defaultMessage="Refused"
      />
    )
  },
  [DOCUMENT_STATUS.SUSPECTED_FRAUD]: {
    background: colors['@success-color-light'],
    color: colors['@success-color-dark'],
    label: <FormattedMessage id="accepted" defaultMessage="Accepted" />,
  },
  DEFAULT: {
    background: colors['@yellow-light-tarnished'],
    color: colors['@text-color']
  }
};

export const SUSPECTED_FRAUD_STYLE = {
  background: '#FBF0E8',
  color: colors['@checkIn-completed'],
};

export const CHECK_IN_STATUS = 'checkInStatus';
export const CHECK_OUT_STATUS = 'checkOutStatus';
export const INCLUDE_MANUAL_CHECK_OUT = 'includeManualCheckOut';

const DASHBOARD_FILTERS_TYPES = {
  CHECK_IN_SENT: {
    label: `${SELF_SERVICE_TYPE.CHECK_IN}-${SELF_SERVICE_STATUS.SENT}`.toLowerCase(),
    value: [CHECK_IN_STATUS, SELF_SERVICE_STATUS.SENT]
  },
  CHECK_IN_COMPLETED: {
    label: `${SELF_SERVICE_TYPE.CHECK_IN}-${SELF_SERVICE_STATUS.ANSWERED}`.toLowerCase(),
    value: [CHECK_IN_STATUS, SELF_SERVICE_STATUS.ANSWERED]
  },
  CHECK_OUT_SENT: {
    label: `${SELF_SERVICE_TYPE.CHECK_OUT}-${SELF_SERVICE_STATUS.SENT}`.toLowerCase(),
    value: [CHECK_OUT_STATUS, SELF_SERVICE_STATUS.SENT]
  },
  CHECK_OUT_COMPLETED: {
    label: `${SELF_SERVICE_TYPE.CHECK_OUT}-${SELF_SERVICE_STATUS.ANSWERED}`.toLowerCase(),
    value: [CHECK_OUT_STATUS, SELF_SERVICE_STATUS.ANSWERED]
  },
  CHECK_OUT_MANUALLY_COMPLETED: {
    label: `${SELF_SERVICE_TYPE.CHECK_OUT}-${SELF_SERVICE_ORIGIN.MANUAL}`.toLowerCase(),
    value: [INCLUDE_MANUAL_CHECK_OUT, true]
  }
};

export const DASHBOARD_FILTERS = Object.values(DASHBOARD_FILTERS_TYPES);

export const SELF_SERVICE_FLAG = {
  DOCUMENTS_ANSWERED: 'documentsAnswered',
  VEHICLE_MOBILITY_FILLED: 'vehicleMobilityFilled',
  IS_MOBILITY_IN_PROGRESS: 'isMobilityInProgress',
  HAS_DELIVERY_DOCUMENT: 'hasDeliveryDocument',
  IS_KEY_RETRIEVED: 'isKeyRetrieved'
};

export const SELF_SERVICE_TYPE_FLAG = {
  [SELF_SERVICE_FLAG.DOCUMENTS_ANSWERED]: SELF_SERVICE_TYPE.CHECK_IN,
  [SELF_SERVICE_FLAG.VEHICLE_MOBILITY_FILLED]: SELF_SERVICE_TYPE.CHECK_IN,
  [SELF_SERVICE_FLAG.IS_MOBILITY_IN_PROGRESS]: SELF_SERVICE_TYPE.CHECK_IN,
  [SELF_SERVICE_FLAG.HAS_DELIVERY_DOCUMENT]: SELF_SERVICE_TYPE.CHECK_OUT,
  [SELF_SERVICE_FLAG.IS_KEY_RETRIEVED]: SELF_SERVICE_TYPE.CHECK_OUT
};

export const SELF_SERVICE_API_STEPS = {
  NONE: 'NONE',
  LOGIN: 'LOGIN',
  LOGIN_HOME: 'LOGIN_HOME',
  CONTACT_INFO: 'CONTACT_INFO',
  REPAIR_ORDER: 'REPAIR_ORDER',
  EXTRAS_ADDITIONAL_SERVICES: 'EXTRAS_ADDITIONAL_SERVICES',
  QUESTIONS: 'QUESTIONS',
  MOBILITY: 'MOBILITY',
  KIOSK_USAGE_QUESTION: 'KIOSK_USAGE_QUESTION',
  SUMMARY_SIGNATURE: 'SUMMARY_SIGNATURE',
  INSTRUCTIONS: 'INSTRUCTIONS',
  REVIEW_PREVIOUS_ANSWERS: 'REVIEW_PREVIOUS_ANSWERS',
  COURTESY_CAR_PARKING: 'COURTESY_CAR_PARKING',
  COURTESY_CAR_INFO: 'COURTESY_CAR_INFO',
  PARKING: 'PARKING',
  VEHICLE_CHECK_QUESTION: 'VEHICLE_CHECK_QUESTION',
  SIGNATURE: 'SIGNATURE',
  DIRECTIONS_TO_KIOSK: 'DIRECTIONS_TO_KIOSK',
  KEYS: 'KEYS',
  GUIDED_VEHICLE_CHECK: 'GUIDED_VEHICLE_CHECK',
  BASKET: 'BASKET',
  FINAL_INSTRUCTIONS: 'FINAL_INSTRUCTIONS',
  APPOINTMENT_SUMMARY: 'APPOINTMENT_SUMMARY',
  FINAL_INVOICE: 'FINAL_INVOICE',
  CUSTOMER_CAR_INFO: 'CUSTOMER_CAR_INFO'
};

export const SELF_SERVICE_STEPS = {
  INSTRUCTIONS: 'INSTRUCTIONS',
  LOGIN: 'LOGIN',
  SUMMARY: 'SUMMARY',
  FINAL_INVOICE: 'FINAL_INVOICE',
  CUSTOMER_INFO: 'CUSTOMER_INFO',
  MOBILITY_PARKING: 'MOBILITY_PARKING',
  MOBILITY_DOCUMENTS: 'MOBILITY_DOCUMENTS',
  BREAKDOWN_INFO: 'BREAKDOWN_INFO',
  REPAIR_ORDER: 'REPAIR_ORDER',
  EXTRAS: 'EXTRAS',
  QUESTIONS: 'QUESTIONS',
  KIOSK_USAGE: 'KIOSK_USAGE',
  COURTESY_VEHICLE: 'COURTESY_VEHICLE',
  PARKING: 'PARKING',
  VEHICLE_CHECK: 'VEHICLE_CHECK',
  BASKET: 'BASKET',
  SIGNATURE: 'SIGNATURE',
  FINAL_INSTRUCTIONS: 'FINAL_INSTRUCTIONS'
};

export const WORKFLOW_TYPES = [
  SELF_SERVICE_TYPE.CHECK_IN,
  QUICK_CHECK_IN,
  SELF_SERVICE_TYPE.CHECK_OUT,
  SELF_SERVICE_TYPE.BREAKDOWN
];

export const SELF_SERVICE_API_STEPS_NAME = createDictionary({
  [SELF_SERVICE_API_STEPS.NONE]: <FormattedMessage id="none" defaultMessage="None" />,
  // as for now home login and login_home are in different tabs and shall have the same translation
  // let's not forget to tackle this if we ever need to have them in the same tab.
  [SELF_SERVICE_API_STEPS.LOGIN]: <FormattedMessage id="login" defaultMessage="Login" />,
  [SELF_SERVICE_API_STEPS.LOGIN_HOME]: <FormattedMessage id="login" defaultMessage="Login" />,
  [SELF_SERVICE_API_STEPS.CONTACT_INFO]: (
    <FormattedMessage
      id="page.config.dealer_general.selfService.workflow.steps.CUSTOMER_INFO.name"
      defaultMessage="Customer information"
    />
  ),
  [SELF_SERVICE_API_STEPS.REPAIR_ORDER]: (
    <FormattedMessage
      id="page.config.dealer_general.selfService.workflow.steps.REPAIR_ORDER.name"
      defaultMessage="Repair order"
    />
  ),
  [SELF_SERVICE_API_STEPS.EXTRAS_ADDITIONAL_SERVICES]: (
    <FormattedMessage
      id="page.stats.selfServiceDetails.steps.extraAdditionalServices"
      defaultMessage="Extras / Additional services"
    />
  ),
  [SELF_SERVICE_API_STEPS.QUESTIONS]: (
    <FormattedMessage
      id="page.config.dealer_general.selfService.workflow.steps.QUESTIONS.name"
      defaultMessage="Questions"
    />
  ),
  [SELF_SERVICE_API_STEPS.MOBILITY]: (
    <FormattedMessage
      id="page.config.dealer_general.selfService.workflow.steps.MOBILITY_DOCUMENTS.name"
      defaultMessage="Mobility question"
    />
  ),
  [SELF_SERVICE_API_STEPS.KIOSK_USAGE_QUESTION]: (
    <FormattedMessage
      id="page.config.dealer_general.selfService.workflow.steps.KIOSK_USAGE.name"
      defaultMessage="Kiosk customer preference"
    />
  ),
  [SELF_SERVICE_API_STEPS.SUMMARY_SIGNATURE]: (
    <FormattedMessage
      id="page.stats.selfServiceDetails.steps.summarySignature"
      defaultMessage="Summary + sign documents"
    />
  ),
  [SELF_SERVICE_API_STEPS.INSTRUCTIONS]: (
    <FormattedMessage
      id="page.config.dealer_general.selfService.workflow.steps.INSTRUCTIONS.name"
      defaultMessage="Starting instruction"
    />
  ),
  [SELF_SERVICE_API_STEPS.REVIEW_PREVIOUS_ANSWERS]: (
    <FormattedMessage
      id="page.stats.selfServiceDetails.steps.reviewPreviousAnswers"
      defaultMessage="Review previous steps"
    />
  ),
  [SELF_SERVICE_API_STEPS.COURTESY_CAR_INFO]: (
    <FormattedMessage
      id="page.config.dealer_general.selfService.workflow.steps.COURTESY_VEHICLE.name"
      defaultMessage="Courtesy vehicle info"
    />
  ),
  [SELF_SERVICE_API_STEPS.COURTESY_CAR_PARKING]: (
    <FormattedMessage
      id="page.config.dealer_general.selfService.workflow.steps.MOBILITY_PARKING.name"
      defaultMessage="Courtesy vehicle parking"
    />
  ),
  [SELF_SERVICE_API_STEPS.PARKING]: (
    <FormattedMessage
      id="page.config.dealer_general.selfService.workflow.steps.PARKING.name"
      defaultMessage="Parking"
    />
  ),
  [SELF_SERVICE_API_STEPS.VEHICLE_CHECK_QUESTION]: (
    <FormattedMessage
      id="page.stats.selfServiceDetails.steps.vehicleCheckQuestion"
      defaultMessage="Vehicle check"
    />
  ),
  [SELF_SERVICE_API_STEPS.SIGNATURE]: (
    <FormattedMessage
      id="page.config.dealer_general.selfService.workflow.steps.SIGNATURE.name"
      defaultMessage="Signature"
    />
  ),
  [SELF_SERVICE_API_STEPS.DIRECTIONS_TO_KIOSK]: (
    <FormattedMessage
      id="page.stats.selfServiceDetails.steps.directionsToKiosk"
      defaultMessage="Directions to kiosk"
    />
  ),
  [SELF_SERVICE_API_STEPS.KEYS]: (
    <FormattedMessage id="page.stats.selfServiceDetails.steps.keys" defaultMessage="Drop/Exchange keys" />
  ),
  [SELF_SERVICE_API_STEPS.GUIDED_VEHICLE_CHECK]: (
    <FormattedMessage
      id="page.stats.selfServiceDetails.steps.guidedVehicleCheck"
      defaultMessage="Guided vehicle check"
    />
  ),
  [SELF_SERVICE_API_STEPS.FINAL_INSTRUCTIONS]: (
    <FormattedMessage
      id="page.config.dealer_general.selfService.workflow.steps.FINAL_INSTRUCTIONS.name"
      defaultMessage="Instructions"
    />
  ),
  [SELF_SERVICE_API_STEPS.APPOINTMENT_SUMMARY]: (
    <FormattedMessage
      id="page.config.dealer_general.selfService.workflow.steps.SUMMARY.name"
      defaultMessage="Appointment summary"
    />
  ),
  [SELF_SERVICE_API_STEPS.FINAL_INVOICE]: (
    <FormattedMessage
      id="page.config.dealer_general.selfService.workflow.steps.FINAL_INVOICE.name"
      defaultMessage="Final invoice"
    />
  ),
  [SELF_SERVICE_API_STEPS.CUSTOMER_CAR_INFO]: (
    <FormattedMessage
      id="page.stats.selfServiceDetails.steps.customerCarParking"
      defaultMessage="Customer car parking"
    />
  ),
  [SELF_SERVICE_API_STEPS.BASKET]: <FormattedMessage id="page.config.dealer_general.selfService.workflow.steps.BASKET.name" defaultMessage="Basket" />
}, <FormattedMessage id="page.home.unknown_status" defaultMessage="N/A" />);

export const NORMALIZED_PERIODS = {
  PT24H: 'P1D',
  PT48H: 'P2D',
  PT72H: 'P3D',
  PT96H: 'P4D',
  PT120H: 'P5D'
};

export const CHECK_IN_LINK_VALIDITY_PERIODS = {
  P1W: 'P1W',
  P2W: 'P2W',
  P1M: 'P1M',
  P2M: 'P2M',
  P3M: 'P3M'
};

export const VALIDATION_ERROR_CODE_MESSAGES: Record<string, React.ReactNode> = {
  // Remarks and "Approved" and "Suspected fraud" tags
  EError_ImageFromScreen: (
    <FormattedMessage
      id="checkIn.mobility.documents.validation.error.EError_ImageFromScreen"
      defaultMessage="The image appears to be a photo of a screen, not an original document"
    />
  ),
  // Remarks and "Approved" tags
  EError_IdNotDetected: (
    <FormattedMessage
      id="checkIn.mobility.documents.validation.error.EError_IdNotDetected"
      defaultMessage="Not all corners of the document are in the frame OR scan is not the asked document"
    />
  ),
  EError_IdPageMissing: (
    <FormattedMessage
      id="checkIn.mobility.documents.validation.error.EError_IdPageMissing"
      defaultMessage="An expected page of the document was not detected"
    />
  ),
  EError_IdIncompleteData: (
    <FormattedMessage
      id="checkIn.mobility.documents.validation.error.EError_IdIncompleteData"
      defaultMessage="At least one information field could not be extracted"
    />
  ),
  // Remarks and "To validate" tags
  EError_IdMatchingFailed: (
    <FormattedMessage
      id="checkIn.mobility.documents.validation.error.EError_IdMatchingFailed"
      defaultMessage="Document scanned failed to match with a known document template"
    />
  ),
  IdNotInList: (
    <FormattedMessage
      id="checkIn.mobility.documents.validation.error.IdNotInList"
      defaultMessage="Document scanned failed to match with a known document template"
    />
  ),
  EError_IdMismatch: (
    <FormattedMessage
      id="checkIn.mobility.documents.validation.error.EError_IdMismatch"
      defaultMessage="Frontside and backside of document do not belong together"
    />
  ),
  EError_IdFaceImageCaptureFailed: (
    <FormattedMessage
      id="checkIn.mobility.documents.validation.error.EError_IdFaceImageCaptureFailed"
      defaultMessage="No face could be detected on the document"
    />
  ),
  EError_IdNoData: (
    <FormattedMessage
      id="checkIn.mobility.documents.validation.error.EError_IdNoData"
      defaultMessage="The document cannot be recognized OR a document has been detected but no data will be extracted"
    />
  ),
  EError_IdExpired: (
    <FormattedMessage
      id="checkIn.mobility.documents.validation.error.EError_IdExpired"
      defaultMessage="User's age is below the minimum or could not be verified"
    />
  ),
  EError_FaceNotRecognised: (
    <FormattedMessage
      id="checkIn.mobility.documents.validation.error.EError_FaceNotRecognised"
      defaultMessage="A face could not be detected on the document or in the selfie video"
    />
  ),
  EError_FaceNotVerified: (
    <FormattedMessage
      id="checkIn.mobility.documents.validation.error.EError_FaceNotVerified"
      defaultMessage="The face from the document do not match with the one from the selfie video"
    />
  ),
  EError_FaceLivenessFailed: (
    <FormattedMessage
      id="checkIn.mobility.documents.validation.error.EError_FaceLivenessFailed"
      defaultMessage="Liveness check failed"
    />
  ),
  NoDocumentRecognized: (
    <FormattedMessage
      id="checkIn.mobility.documents.validation.error.NoDocumentRecognized"
      defaultMessage="No document has been recognized"
    />
  ),
};

export const filterSupportedErrors = (errors: string[]): string[] =>
  errors.filter(error => typeof VALIDATION_ERROR_CODE_MESSAGES[error] !== 'undefined');
